import { z } from 'zod';
import type { EngageSequence } from '@/store/features/engage.feature';
import type { ShareableUser } from '@/types';
import type { SequenceOwner } from './use-sequence-owners.hook';

export const STATUS_FILTERS = [
  'all',
  'active',
  'draft',
  'paused',
  'archived',
] as const;

export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** What narrows the library: all of it runs on the rows already loaded. */
export interface SequenceFilters {
  query: string;
  status: StatusFilter;
  /** A user id; null is every owner. */
  owner: string | null;
  favoritesOnly: boolean;
}

/**
 * What /sequences keeps in its URL. A value that is not one of these falls
 * back to the unfiltered list instead of an error page.
 */
export const sequenceListSearchSchema = z.object({
  q: z.string().max(200).optional().catch(undefined),
  status: z.enum(STATUS_FILTERS).optional().catch(undefined),
  owner: z.string().max(200).optional().catch(undefined),
  favorites: z.boolean().optional().catch(undefined),
});

export const NO_FILTERS: SequenceFilters = {
  query: '',
  status: 'all',
  owner: null,
  favoritesOnly: false,
};

export function isStatusFilter(value: unknown): value is StatusFilter {
  return STATUS_FILTERS.includes(value as StatusFilter);
}

export function filtersApplied(filters: SequenceFilters): boolean {
  return (
    Boolean(filters.query.trim()) ||
    filters.status !== 'all' ||
    filters.owner !== null ||
    filters.favoritesOnly
  );
}

/**
 * The rows a set of filters leaves. "All statuses" leaves archived ones out:
 * they are in the library only when asked for. The name match is a
 * case-insensitive substring, the same rule the API's search applies.
 */
export function filterSequences(
  sequences: readonly EngageSequence[],
  filters: SequenceFilters
): EngageSequence[] {
  const needle = filters.query.trim().toLowerCase();
  return sequences.filter((item) => {
    if (needle && !item.name.toLowerCase().includes(needle)) return false;
    if (filters.status === 'all') {
      if (item.archived) return false;
    } else if (item.status.toLowerCase() !== filters.status) return false;
    if (filters.owner !== null && item.owner_user_id !== filters.owner)
      return false;
    if (filters.favoritesOnly && !item.is_favorite) return false;
    return true;
  });
}

/** How many sequences each status choice holds. "All" excludes archived,
 *  as the list does. */
export function statusCounts(
  sequences: readonly EngageSequence[]
): Record<StatusFilter, number> {
  const counts: Record<StatusFilter, number> = {
    all: 0,
    active: 0,
    draft: 0,
    paused: 0,
    archived: 0,
  };
  for (const item of sequences) {
    const key = item.status.toLowerCase();
    if (isStatusFilter(key) && key !== 'all') counts[key] += 1;
    if (!item.archived) counts.all += 1;
  }
  return counts;
}

const FORMER_MEMBER = 'Former member';

/**
 * The owner menu's people: the members, then each owner of a sequence who is
 * no longer one, so their sequences can still be filtered to. Owners are only
 * called former once the membership list has loaded.
 */
export function ownerChoices(
  sequences: readonly EngageSequence[],
  members: readonly ShareableUser[],
  resolveOwner: (userId: string) => SequenceOwner
): ShareableUser[] {
  const known = new Set(members.map((member) => member.user_id));
  const former: ShareableUser[] = [];
  for (const item of sequences) {
    const id = item.owner_user_id;
    if (!id || known.has(id)) continue;
    known.add(id);
    const owner = resolveOwner(id);
    if (owner.resolved || owner.name !== FORMER_MEMBER) continue;
    former.push({ user_id: id });
  }
  return [
    ...members,
    ...former.map((user, index) => ({
      ...user,
      first_name:
        former.length > 1 ? `${FORMER_MEMBER} ${index + 1}` : FORMER_MEMBER,
    })),
  ];
}
